import * as _ from 'lodash-es';
import type { Reducer } from 'react';
import { useReducer } from 'react';
import {
  AREAS_MIN_WIDTH,
  MAGNETIC_WALL_COLLAPSED_WIDTH,
  MAGNETIC_WALL_MAX_WIDTH,
  MAGNETIC_WALL_MIN_WIDTH,
  POST_SIDEBAR_SHIFT_BREAKPOINT,
  PRIORITY_LIST_MAX_WIDTH,
  SIDEBAR_WIDTH,
  STATIC_AREAS_WIDTH,
  TOC_COLLAPSED_WIDTH,
  TOC_MIN_WIDTH,
} from './PostLayoutSizes';
import exhaustiveMatchGuard from '../utils/exhaustiveMatchGuard';

type Area = 'sidebar' | 'magneticWall' | 'toc';
type PriorityList = [Area, Area, Area] | [Area, Area] | [Area] | [];

const DEFAULT_PRIORITY_LIST: PriorityList = ['sidebar', 'toc', 'magneticWall'];

interface PostLayoutState {
  expandedAreas: Set<Area>;
  priorityList: PriorityList;
  lastWidth: number | null;
  // TODO: Post layout tests
  // hasMagneticWallItems: boolean;
  // hasHeadings: boolean;
  hasMagneticWallItems: boolean | null;
  hasHeadings: boolean | null;
  isTOCDisabled?: boolean;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const INITIAL_STATE: PostLayoutState = {
  expandedAreas: new Set<Area>(),
  priorityList: DEFAULT_PRIORITY_LIST,
  lastWidth: null,
  // TODO: Post layout tests
  // hasMagneticWallItems: false,
  // hasHeadings: false,
  hasMagneticWallItems: null,
  hasHeadings: null,
};

type PostLayoutAction =
  | {
      type: 'TOGGLE_AREA';
      payload: {
        area: Area;
        isExpanded: boolean;
      };
    }
  | {
      type: 'RESIZE';
      payload: number;
    }
  | {
      type: 'SET_HAS_MAGNETIC_WALL_ITEMS';
      payload: boolean;
    }
  | {
      type: 'SET_HAS_HEADINGS';
      payload: boolean;
    };

const prioritizeArea = (area: Area, priorityList: PriorityList) =>
  _.uniq([area, ...priorityList]) as PriorityList;

const deprioritizeArea = (area: Area, priorityList: PriorityList) =>
  _.without(priorityList, area) as PriorityList;

const expandArea = (area: Area, expandedAreas: Set<Area>) => {
  const newSet = new Set(expandedAreas);
  newSet.add(area);
  return newSet;
};

const collapseArea = (area: Area, expandedAreas: Set<Area>) => {
  const newSet = new Set(expandedAreas);
  newSet.delete(area);
  return newSet;
};

const canTOCExpand = (currentWidth: number) =>
  currentWidth >=
  STATIC_AREAS_WIDTH + TOC_MIN_WIDTH + MAGNETIC_WALL_COLLAPSED_WIDTH;

const canMagneticWallExpand = (currentWidth: number) =>
  currentWidth >= STATIC_AREAS_WIDTH + TOC_MIN_WIDTH + MAGNETIC_WALL_MAX_WIDTH;

const recalculateState = ({
  currentWidth,
  state,
}: {
  currentWidth: number | null;
  state: PostLayoutState;
}): PostLayoutState => {
  if (!currentWidth) return state;

  const {
    lastWidth,
    priorityList,
    expandedAreas,
    hasMagneticWallItems,
    hasHeadings,
    isTOCDisabled,
  } = state;

  // Note that this breakpoint is the same whether the sidebar is expanded or collapsed.
  // While it's technically still possible to show both MW and TOC below that point when the sidebar is collapsed,
  // we decided to keep this breakpoint the same for both cases.
  const isTogglingEnabled =
    currentWidth >=
    STATIC_AREAS_WIDTH +
      SIDEBAR_WIDTH +
      TOC_MIN_WIDTH +
      MAGNETIC_WALL_COLLAPSED_WIDTH;

  const shouldOpenSidebar =
    lastWidth != null &&
    lastWidth < POST_SIDEBAR_SHIFT_BREAKPOINT &&
    currentWidth >= POST_SIDEBAR_SHIFT_BREAKPOINT;

  const shouldResetPriorityList =
    lastWidth != null &&
    lastWidth < PRIORITY_LIST_MAX_WIDTH &&
    currentWidth >= PRIORITY_LIST_MAX_WIDTH;

  const nextPriorityList = (() => {
    if (shouldResetPriorityList) {
      return DEFAULT_PRIORITY_LIST;
    }

    if (shouldOpenSidebar) {
      return prioritizeArea('sidebar', priorityList);
    }

    return priorityList;
  })();

  const result = nextPriorityList.reduce(
    (acc, area) => {
      if (area === 'sidebar') {
        const nextWidth = acc.width + SIDEBAR_WIDTH;
        const isExpanded =
          (shouldOpenSidebar || expandedAreas.has('sidebar')) &&
          nextWidth <= currentWidth;

        const nextExpandedAreas = isExpanded
          ? expandArea('sidebar', acc.expandedAreas)
          : collapseArea('sidebar', acc.expandedAreas);

        return {
          ...acc,
          expandedAreas: nextExpandedAreas,
          width: nextExpandedAreas.has('sidebar') ? nextWidth : acc.width,
        };
      }

      if (area === 'toc') {
        const nextWidth = acc.width - TOC_COLLAPSED_WIDTH + TOC_MIN_WIDTH;
        const shouldExpand =
          canTOCExpand(currentWidth) &&
          !isTOCDisabled &&
          hasHeadings &&
          nextWidth <= currentWidth &&
          (!acc.expandedAreas.has('magneticWall') || isTogglingEnabled);

        if (shouldExpand) {
          return {
            ...acc,
            expandedAreas: expandArea('toc', acc.expandedAreas),
            width: nextWidth,
          };
        }

        return {
          ...acc,
          expandedAreas: collapseArea('toc', acc.expandedAreas),
        };
      }

      if (area === 'magneticWall') {
        const nextWidth =
          acc.width - MAGNETIC_WALL_COLLAPSED_WIDTH + MAGNETIC_WALL_MIN_WIDTH;

        const shouldExpand =
          (!acc.expandedAreas.has('toc') || isTogglingEnabled) &&
          hasMagneticWallItems &&
          canMagneticWallExpand(currentWidth) &&
          nextWidth <= currentWidth;

        if (shouldExpand) {
          return {
            ...acc,
            expandedAreas: expandArea('magneticWall', acc.expandedAreas),
            width: nextWidth,
          };
        }

        return {
          ...acc,
          expandedAreas: collapseArea('magneticWall', acc.expandedAreas),
        };
      }
      return acc;
    },
    {
      width: AREAS_MIN_WIDTH,
      expandedAreas: new Set<Area>([]),
    },
  );

  return {
    ...state,
    priorityList: nextPriorityList,
    expandedAreas: result.expandedAreas,
  };
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const reducer: Reducer<PostLayoutState, PostLayoutAction> = (
  state: PostLayoutState,
  action: PostLayoutAction,
) => {
  switch (action.type) {
    case 'SET_HAS_HEADINGS': {
      if (state.hasHeadings === action.payload) return state;

      if (state.hasHeadings == null) {
        return recalculateState({
          currentWidth: state.lastWidth,
          state: {
            ...state,
            hasHeadings: action.payload,
          },
        });
      }

      return {
        ...state,
        hasHeadings: action.payload,
      };

      // TODO: Post layout tests
      // const shouldExpand =
      //   state.lastWidth != null &&
      //   canTOCExpand(state.lastWidth) &&
      //   !state.hasHeadings &&
      //   action.payload;

      // const expandedAreas = shouldExpand
      //   ? expandArea('toc', state.expandedAreas)
      //   : collapseArea('toc', state.expandedAreas);

      // return recalculateState({
      //   currentWidth: state.lastWidth,
      //   state: {
      //     ...state,
      //     expandedAreas,
      //     hasHeadings: action.payload,
      //   },
      // });
    }
    case 'SET_HAS_MAGNETIC_WALL_ITEMS': {
      const hasMagneticWallItemsNext = action.payload;
      if (state.hasMagneticWallItems === hasMagneticWallItemsNext) return state;

      if (state.hasMagneticWallItems == null) {
        return recalculateState({
          currentWidth: state.lastWidth,
          state: {
            ...state,
            hasMagneticWallItems: action.payload,
          },
        });
      }

      return {
        ...state,
        hasMagneticWallItems: action.payload,
      };

      // TODO: Post layout tests
      // const shouldExpand =
      //   state.lastWidth != null &&
      //   canMagneticWallExpand(state.lastWidth) &&
      //   !state.hasMagneticWallItems &&
      //   hasMagneticWallItemsNext;

      // const expandedAreas = shouldExpand
      //   ? expandArea('magneticWall', state.expandedAreas)
      //   : collapseArea('magneticWall', state.expandedAreas);

      // return recalculateState({
      //   currentWidth: state.lastWidth,
      //   state: {
      //     ...state,
      //     expandedAreas,
      //     hasMagneticWallItems: hasMagneticWallItemsNext,
      //   },
      // });
    }
    case 'TOGGLE_AREA': {
      const { area, isExpanded } = action.payload;
      if (
        (isExpanded && state.expandedAreas.has(area)) ||
        (!isExpanded && !state.expandedAreas.has(area)) ||
        (area === 'toc' &&
          state.lastWidth != null &&
          !canTOCExpand(state.lastWidth)) ||
        (area === 'magneticWall' &&
          state.lastWidth != null &&
          !canMagneticWallExpand(state.lastWidth))
      ) {
        return state;
      }

      const expandedAreas = isExpanded
        ? expandArea(area, state.expandedAreas)
        : collapseArea(area, state.expandedAreas);

      // user action, therefore priority changes
      const priorityList = isExpanded
        ? prioritizeArea(area, state.priorityList)
        : deprioritizeArea(area, state.priorityList);

      const result = recalculateState({
        currentWidth: state.lastWidth,
        state: {
          ...state,
          priorityList,
          expandedAreas,
        },
      });

      // we actually wanted to collapse it, but the calculation might have yielded
      // that the area should be expanded, so we collapse it again
      if (!isExpanded && result.expandedAreas.has('toc')) {
        return {
          ...state,
          ...result,
          expandedAreas: collapseArea(area, result.expandedAreas),
        };
      }

      return {
        ...state,
        ...result,
      };
    }
    case 'RESIZE': {
      const currentWidth = action.payload;

      return {
        ...recalculateState({
          currentWidth,
          state,
        }),
        lastWidth: action.payload,
      };
    }
    default: {
      exhaustiveMatchGuard(action);
      return state;
    }
  }
};

const usePostLayoutStateReducer = ({
  isSidebarOpenInitially,
  isTOCDisabled,
}: {
  isSidebarOpenInitially: boolean;
  isTOCDisabled?: boolean;
}) => {
  const [state, dispatch] = useReducer(reducer, {
    ...INITIAL_STATE,
    isTOCDisabled,
    expandedAreas: isSidebarOpenInitially
      ? new Set(['sidebar' as const])
      : new Set([]),
  });

  return {
    state,
    dispatch,
    canMagneticWallExpand:
      state.lastWidth != null && canMagneticWallExpand(state.lastWidth),
    canTOCExpand:
      !isTOCDisabled &&
      state.lastWidth != null &&
      canTOCExpand(state.lastWidth),
  };
};

export default usePostLayoutStateReducer;
